import { useState } from 'react';
import { Eye, EyeOff, AlertCircle, Loader2, Apple, Chrome } from 'lucide-react';
import { Logo } from './Logo';
import { useAuth } from '../context/AuthContext';
import { clearPendingSignupRef, setPendingSignupRef } from '../utils/apiClient';
import { GOOGLE_AUTH_ENABLED, APPLE_AUTH_ENABLED } from '../utils/authProviders';
import { clearSignupFinishing, markSignupFinishing } from '../utils/signupFlow';
import { TRIAL_DAYS } from '../utils/planPricing';

export function SignUp({ onSwitchToSignIn, onSignUpSuccess, signupRef = '' }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const { signUp, signInWithGoogle, signInWithApple } = useAuth();
  const normalizedSignupRef = typeof signupRef === 'string' ? signupRef.trim().toLowerCase() : '';
  const isTrial = normalizedSignupRef === 'trial';

  const passwordError = (pw) => {
    if (!pw) return 'Password is required';
    if (pw.length < 8) return 'Password must be at least 8 characters';
    if (!/[a-z]/.test(pw)) return 'Add at least one lowercase letter';
    if (!/[A-Z]/.test(pw)) return 'Add at least one uppercase letter';
    if (!/[0-9]/.test(pw)) return 'Add at least one number';
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pw)) return 'Add at least one symbol';
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    const pwErr = passwordError(formData.password);
    if (pwErr) {
      newErrors.password = pwErr;
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
      if (isTrial) {
        setPendingSignupRef(normalizedSignupRef);
      } else {
        clearPendingSignupRef();
      }
      // Hold the signed-in redirect until onboarding is ready (see utils/signupFlow.js).
      markSignupFinishing();
      await signUp(formData.email, formData.password, formData.name);
      await onSignUpSuccess?.();
    } catch (error) {
      console.error('Sign up error:', error);
      clearSignupFinishing();
      const message = error?.message || 'Sign up failed. Please try again.';
      setErrors({ general: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProvider = async (providerFn) => {
    setIsSocialLoading(true);
    setErrors({});
    try {
      if (isTrial) {
        setPendingSignupRef(normalizedSignupRef);
      } else {
        clearPendingSignupRef();
      }
      markSignupFinishing();
      await providerFn();
      await onSignUpSuccess?.();
    } catch (error) {
      console.error('Social sign up error:', error);
      clearSignupFinishing();
      const message = error?.message || 'Sign up failed. Please try again.';
      setErrors({ general: message });
    } finally {
      setIsSocialLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Brand Header */}
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <Logo className="w-16 h-16" color="#AFBB8B" />
          </div>
          <h1 className="text-4xl font-bold text-[#6B7A54] mb-2">
            MealPlan Genie
          </h1>
          <p className="text-[#757575]">Create your account</p>
          {isTrial && (
            <p className="mt-3 inline-flex rounded-full bg-[#F0F3EC] px-4 py-2 text-sm font-medium text-[#6B7A54] shadow-sm">
              {TRIAL_DAYS}-day free trial. No credit card required.
            </p>
          )}
        </div>

        {/* Sign Up Form */}
        <div className="bg-white rounded-3xl shadow-sm p-8">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* General Error */}
            {errors.general && (
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                <span className="text-sm text-[#B85A5A]">{errors.general}</span>
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-2xl border ${
                  errors.name ? 'border-[#B85A5A]' : 'border-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors`}
                placeholder="Your name"
              />
              {errors.name && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span className="text-sm text-[#B85A5A]">{errors.name}</span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 rounded-2xl border ${
                  errors.email ? 'border-[#B85A5A]' : 'border-gray-200'
                } focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors`}
                placeholder="you@example.com"
              />
              {errors.email && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span className="text-sm text-[#B85A5A]">{errors.email}</span>
                </div>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-2xl border ${
                    errors.password ? 'border-[#B85A5A]' : 'border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors pr-12`}
                  placeholder="Min 8 chars, upper, lower, number, symbol"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.password && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span className="text-sm text-[#B85A5A]">{errors.password}</span>
                </div>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-2xl border ${
                    errors.confirmPassword ? 'border-[#B85A5A]' : 'border-gray-200'
                  } focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors pr-12`}
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="flex items-center gap-1.5 mt-2">
                  <AlertCircle className="w-4 h-4 text-[#B85A5A]" />
                  <span className="text-sm text-[#B85A5A]">{errors.confirmPassword}</span>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || isSocialLoading}
              className="w-full py-3 bg-primary text-[#2D3319] rounded-full hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          {(GOOGLE_AUTH_ENABLED || APPLE_AUTH_ENABLED) && (
            <div className="mt-4 grid grid-cols-1 gap-3">
              {GOOGLE_AUTH_ENABLED && (
                <button
                  type="button"
                  disabled={isSocialLoading || isLoading}
                  onClick={() => handleProvider(signInWithGoogle)}
                  className="w-full py-3 bg-white border border-gray-200 text-gray-900 rounded-full hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {isSocialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Chrome className="w-4 h-4" />}
                  Continue with Google
                </button>
              )}
              {APPLE_AUTH_ENABLED && (
                <button
                  type="button"
                  disabled={isSocialLoading || isLoading}
                  onClick={() => handleProvider(signInWithApple)}
                  className="w-full py-3 bg-black text-white rounded-full hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
                >
                  {isSocialLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Apple className="w-4 h-4" />}
                  Continue with Apple
                </button>
              )}
            </div>
          )}

          {/* Switch to Sign In */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#757575]">
              Already have an account?{' '}
              <button
                onClick={onSwitchToSignIn}
                className="text-[#B85A5A] hover:text-[#A04848] font-medium transition-colors underline"
              >
                Sign In
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}